import { useEffect, useRef, useState } from "react";
import type { PointerEvent, ReactElement } from "react";
import type { ProgressionInning } from "../result/derive";

export type ChartKind = "man" | "rr" | "worm";
export type ChartSeries = { name: string; color: string; inning: ProgressionInning };

type Pt = { o: number; r: number; w: number; cum: number; rr: number };

const points = (inn: ProgressionInning): Pt[] => {
  let balls = 0;
  return inn.overs.map((o, i) => {
    // a missing per-over count (older backend) falls back to six, and the final over takes whatever is left of the innings total
    const known = o.legalBalls ?? (i === inn.overs.length - 1 ? Math.max(0, inn.legalBalls - balls) || 6 : 6);
    balls += known;
    return { o: o.over, r: o.runs, w: o.wickets, cum: o.cumRuns, rr: balls ? o.cumRuns / (balls / 6) : 0 };
  });
};

const useWidth = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(el.clientWidth);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
};

const LABEL = { man: "RUNS PER OVER", rr: "RUN RATE", worm: "RUNS" } as const;

/** Over-by-over chart shared by Manhattan, run rate and worm. Hover (or tap) an over to compare both sides. */
const OverChart = ({ kind, series, target, noOvers }: { kind: ChartKind; series: ChartSeries[]; target?: number; noOvers: number }) => {
  const [ref, cw] = useWidth();
  const [hover, setHover] = useState<number | null>(null);
  const data = series.map((s) => ({ ...s, pts: points(s.inning) }));
  const val = (d: Pt) => (kind === "man" ? d.r : kind === "rr" ? d.rr : d.cum);
  const W = Math.max(300, cw || 760), H = W < 500 ? 220 : 280, pl = 40, pr = 14, pt = 16, pb = 40;
  const NO = Math.max(noOvers, ...data.map((s) => s.pts.length), 1);
  const slot = (W - pl - pr) / NO;

  let mx = Math.max(...data.flatMap((s) => s.pts.map(val)), kind === "worm" ? target ?? 0 : 0, 1);
  if (kind === "man") mx = Math.max(12, Math.ceil(mx / 4) * 4 + (data.some((s) => s.pts.some((d) => d.w && d.r >= mx - 2)) ? 4 : 0));
  else if (kind === "rr") mx = Math.max(12, Math.ceil(mx / 2) * 2);
  else mx = Math.max(50, Math.ceil(mx / 50) * 50);
  const step = kind === "worm" ? 50 : kind === "man" ? 4 : 2;
  const X = (o: number) => pl + slot * o + slot / 2; // centre of over o (0-based)
  const Xe = (o: number) => pl + slot * (o + 1); // end of over o
  const Y = (v: number) => pt + (1 - v / mx) * (H - pt - pb);
  const labels = W < 520 ? [1, 5, 10, 15, 20].filter((n) => n <= NO) : Array.from({ length: NO }, (_, i) => i + 1);

  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    const rc = e.currentTarget.getBoundingClientRect();
    const vx = ((e.clientX - rc.left) / rc.width) * W;
    const o = Math.floor((vx - pl) / slot);
    setHover(o < 0 || o >= NO ? null : o);
  };

  const gridLines: ReactElement[] = [];
  for (let v = 0; v <= mx; v += step) {
    gridLines.push(
      <g key={v}>
        <line x1={pl} x2={W - pr} y1={Y(v)} y2={Y(v)} stroke="#EFF0F3" />
        <text x={pl - 8} y={Y(v) + 4} textAnchor="end" fontSize={11} fill="#9AA1AE">{v}</text>
      </g>,
    );
  }

  const scale = (cw || W) / W;
  const px = (pl + slot * ((hover ?? 0) + 1)) * scale;
  const tipLeft = Math.max(4, px + 190 > (cw || W) ? px - slot * scale - 200 : px + 8);
  const tip = hover === null ? null : (
    <div className="ptip on" style={{ left: tipLeft, top: 8 }}>
      <div style={{ fontWeight: 800, marginBottom: 3 }}>Over {hover + 1}</div>
      {data.map((s) => {
        const d = s.pts.find((p) => p.o - 1 === hover) ?? s.pts[hover];
        return (
          <div key={s.name}>
            <span className="sw" style={{ background: s.color }} />
            {s.name}:{" "}
            {d ? (
              kind === "man" ? <><b>{d.r}</b> run{d.r === 1 ? "" : "s"}{d.w ? ` · ${d.w} wkt` : ""}</> : kind === "rr" ? <><b>{d.rr.toFixed(2)}</b> after the over</> : <><b>{d.cum}</b> runs</>
            ) : "–"}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="pa-chart" ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${kind === "man" ? "Runs per over" : kind === "rr" ? "Run rate after each over" : "Cumulative runs"} for both innings`} onPointerMove={onMove} onPointerLeave={() => setHover(null)}>
        {gridLines}
        {kind === "man" ? labels.map((o) => <text key={o} x={X(o - 1)} y={H - pb + 16} textAnchor="middle" fontSize={11} fill="#687080">{o}</text>) : (
          <>
            <text x={pl} y={H - pb + 16} textAnchor="middle" fontSize={11} fill="#687080">0</text>
            {labels.map((o) => <text key={o} x={Xe(o - 1)} y={H - pb + 16} textAnchor="middle" fontSize={11} fill="#687080">{o}</text>)}
          </>
        )}
        <text x={(pl + W - pr) / 2} y={H - 6} textAnchor="middle" fontSize={10.5} fontWeight={700} letterSpacing=".8" fill="#9AA1AE">OVERS</text>
        <text transform={`translate(11 ${(pt + H - pb) / 2}) rotate(-90)`} textAnchor="middle" fontSize={10.5} fontWeight={700} letterSpacing=".8" fill="#9AA1AE">{LABEL[kind]}</text>
        <line x1={pl} x2={W - pr} y1={Y(0)} y2={Y(0)} stroke="#C9CCD4" />
        {hover !== null ? <rect x={pl + slot * hover} y={pt} width={slot} height={H - pt - pb} fill="#29276B" opacity={0.05} /> : null}
        {kind === "worm" && target ? (
          <>
            <line x1={pl} x2={W - pr} y1={Y(target)} y2={Y(target)} stroke="#9AA1AE" strokeDasharray="5 4" />
            <text x={W - pr} y={Y(target) - 6} textAnchor="end" fontSize={11} fontWeight={700} fill="#687080">Target {target}</text>
          </>
        ) : null}
        {kind === "man"
          ? data.map((s, i) => {
              const bw = Math.min(16, slot * 0.36);
              return s.pts.map((d) => {
                const x = X(d.o - 1) + (i ? 1 : -bw - 1), y = Y(d.r);
                return (
                  <g key={`${i}-${d.o}`}>
                    {d.r > 0 ? <path d={`M${x} ${Y(0)}V${Math.min(y + 3, Y(0))}a3 3 0 0 1 3-3h${bw - 6}a3 3 0 0 1 3 3V${Y(0)}Z`} fill={s.color} /> : null}
                    {Array.from({ length: d.w }).map((_, j) => <circle key={j} cx={x + bw / 2} cy={y - 8 - j * 11} r={4} fill="#fff" stroke={s.color} strokeWidth={2} />)}
                  </g>
                );
              });
            })
          : data.map((s) => {
              const pts: [number, number][] = s.pts.map((d) => [Xe(d.o - 1), Y(val(d))]);
              if (kind === "worm") pts.unshift([pl, Y(0)]);
              return (
                <g key={s.name}>
                  <path d={pts.map((p, j) => `${j ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join("")} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                  {s.pts.filter((d) => d.w).map((d) => <circle key={d.o} cx={Xe(d.o - 1)} cy={Y(val(d))} r={4.5} fill="#fff" stroke={s.color} strokeWidth={2} />)}
                </g>
              );
            })}
      </svg>
      {tip}
    </div>
  );
};

export default OverChart;
